/**
 * PinnedGoalsCard — the goals the studio pinned to Today. Hides
 * itself entirely if nothing is pinned, so the dashboard doesn't
 * carry empty real estate.
 *
 * PASSPORT-2 — pinned goals are ordinary Passport goals with
 * `pinned = true`: the same rings, the same counters, the same world.
 * Each row is the goal's rings as dots, the ring the headline talks
 * about, and a way into the galaxy.
 *
 * Web parity for the iOS PinnedGoalsSection on Today.
 *
 * PASSPORT-PIN-1 (Sep 28) — `pinned` is set by the "Show on Today"
 * switch (`passport/TodayPinSwitch`): on by default in GoalComposer,
 * flippable on the goal sheet. Before that nothing on the web could
 * set it, so this card never rendered for anyone.
 */
import { Target } from 'lucide-react';
import { OrbitIcon } from '@/components/icons/OrbitIcon';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { worldOf } from '@/components/galaxy3d/worlds';
import { COUNTERS, RING_INK_HEX, formatCount, goalLive, ringDone, ringProgress, useGoals, type CounterMetric, type Goal, type GoalRing } from '@/services/passport';

export const PinnedGoalsCard = () => {
  const { data: allGoals = [], isLoading } = useGoals();
  const goals = allGoals.filter((g) => g.pinned && goalLive(g) && !g.completed_at);
  const nudge = nearestFinishNudge(goals);

  if (isLoading || goals.length === 0) return null;

  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <OrbitIcon name="target" fallback={Target} className="w-4 h-4 text-orbit-orange" />
          <h2 className="font-display text-lg font-medium text-foreground">Goals</h2>
          <span className="text-xs text-muted-foreground">{goals.length}</span>
        </div>
        <Link to="/galaxy" className="text-xs text-lunar-teal hover:underline">
          Open Passport
        </Link>
      </div>

      {nudge && <p className="text-sm font-medium text-foreground">{nudge}</p>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {goals.map((goal) => (
          <PinnedGoalRow key={goal.id} goal={goal} />
        ))}
      </div>
    </section>
  );
};

/** The ring the row talks about: the first open one, counters first. */
function focusRing(g: Goal): GoalRing | null {
  const open = g.rings.filter((r) => !ringDone(r) && ringProgress(r) < 1);
  return open.find((r) => r.kind === 'counter') ?? open[0] ?? g.rings[0] ?? null;
}

function progressLabel(g: Goal): string {
  const r = focusRing(g);
  if (r && r.kind === 'counter' && r.metric && r.target) {
    const v = ringDone(r) ? r.target : Math.min(r.value ?? 0, r.target);
    return `${formatCount(r.metric, v)} / ${formatCount(r.metric, r.target)} ${COUNTERS[r.metric].unit}`;
  }
  const closed = g.rings.filter((x) => ringDone(x) || ringProgress(x) >= 1).length;
  return r ? `${closed} of ${g.rings.length} steps · ${r.label}` : `${closed} of ${g.rings.length} steps`;
}

/**
 * PASSPORT-3 goal nudge — how far is left on the counted goal nearest
 * the finish: "3 projects to go." / "$8,000 to go." / "One more
 * project and it's done." ONE goal (the smallest remaining gap), only
 * counted goals, and only once the counter has moved — "12 projects
 * to go" on the day you set the goal is a receipt, not encouragement.
 */
function nearestFinishNudge(goals: Goal[]): string | null {
  let best: { remaining: number; metric: NonNullable<GoalRing['metric']> } | null = null;
  for (const g of goals) {
    for (const r of g.rings) {
      if (r.kind !== 'counter' || !r.metric || !r.target || ringDone(r)) continue;
      const value = r.value ?? 0;
      if (value <= 0) continue; // the counter hasn't moved yet
      const remaining = r.target - Math.min(value, r.target);
      if (remaining <= 0) continue;
      if (!best || remaining < best.remaining) best = { remaining, metric: r.metric };
    }
  }
  if (!best) return null;
  if (COUNTERS[best.metric].money) return `${formatCount(best.metric, best.remaining)} to go.`;
  const noun: Record<Exclude<CounterMetric, 'revenue' | 'expenses'>, string> = { bookings: 'project', clients: 'client', galleries: 'gallery' };
  const n = noun[best.metric as keyof typeof noun] ?? 'step';
  if (best.remaining === 1) return `One more ${n} and it’s done.`;
  return `${best.remaining} ${n}s to go.`;
}

const PinnedGoalRow = ({ goal }: { goal: Goal }) => {
  const world = worldOf(goal);
  const frac = goal.rings.length ? goal.rings.reduce((s, r) => s + ringProgress(r), 0) / goal.rings.length : 0;
  const focus = focusRing(goal);
  const ink = RING_INK_HEX[focus?.ink ?? 'ops'];

  return (
    <Link to={`/galaxy?goal=${goal.id}`} className="block rounded-xl border border-border bg-background/40 p-4 space-y-3 transition-colors hover:bg-background/70">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${ink}1f`, color: ink }}>
          <span className="flex gap-0.5">
            {goal.rings.map((r) => (
              <i key={r.id} className={cn('block h-1.5 w-1.5 rounded-full', ringDone(r) || ringProgress(r) >= 1 ? '' : 'opacity-35')} style={{ background: RING_INK_HEX[r.ink] }} />
            ))}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[.16em] text-muted-foreground">{world.name}</p>
          <p className="font-medium text-sm text-foreground line-clamp-2">{goal.title}</p>
          {goal.why && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{goal.why}</p>}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="h-1.5 rounded-full bg-border/60 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(4, frac * 100)}%`, background: ink }} />
        </div>
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span className="font-medium">{progressLabel(goal)}</span>
          {goal.due_on && <span>by {format(new Date(`${goal.due_on}T12:00:00`), 'MMM d')}</span>}
        </div>
      </div>
    </Link>
  );
};

export default PinnedGoalsCard;
